import { apiClient } from './api';
import type {
  BulkDeleteResponse,
  DeleteAllResponse,
  Phase,
  Region,
  RegionPlacement,
} from '@/types/api';
import type { BodyHeightDefinition } from '@/types/trajectory';
// The editor-side Annotation shape (goal configs, tracking, etc.) — NOT
// types/api.ts's Annotation (a different, backend-echo shape with
// video_id/regions). CreateTemplate.tsx builds requests using this shape and
// passes them straight through to these API functions, so the request/
// response types here must match what the canvas editor actually produces.
import type { Annotation } from '@/components/canvas';
import type { PhaseConfig } from '@/components/phase';
import type {
  CalibrationGradeLabel,
  CalibrationTake,
  CalibrationTakeLabel,
  CalibrationTakeListResponse,
  CalibrationTakeUploadResponse,
  CalibrationTakeDeleteResponse,
  CalibrationRecalibrateResponse,
  CalibrationSummary,
  CalibrationTakeUpdateRequest,
  ExamplePerformanceListResponse,
  PricingDimName,
} from '@/types/calibrationTake';
import type { MediaTokenResponse } from './videoApi';
import type {
  AcceptSentimentOverride,
  CompileRulesAcceptRequest,
  CompileRulesAcceptResponse,
  CompileRulesDismissRequest,
  CompileRulesDismissResponse,
  CompileRulesKickoffResponse,
  CompileRulesStatusResponse,
  DroppedCandidate,
  RefusedRuleDetail,
} from '@/types/ruleCompiler';

/**
 * Template API service for annotation-based templates
 *
 * These templates are created via the CreateTemplate flow and use
 * the /api/templates endpoints (not /api/phases/templates).
 */

/**
 * One far-side checkpoint note (coach-only template metadata, computed at
 * finalize). `visibility_min`/`visibility_max` are the min/max over the goal's
 * landmarks of the fraction of event-window frames in which that landmark was
 * clearly visible in the reference video — diagnostics for the backend, NEVER
 * rendered to the coach (see docs/COACH_FACING_COPY_STANDARD.md §2: numbers
 * only when the coach can act on them).
 */
export interface FarSideNoteEntry {
  template_annotation_id: string;
  /** The checkpoint name the coach typed (or its description). */
  label: string;
  landmarks: string[];
  visibility_min: number;
  visibility_max: number;
}

/**
 * One angle check in the finalize-time tolerance review (observability
 * Phase 4, study metric M8 / ledger HC-26).
 */
export interface ToleranceReviewGoal {
  template_annotation_id: string;
  /** The checkpoint name the coach typed (or its description). */
  label: string;
  /** Which kind of angle check this is. Internal — never rendered. */
  goal_type: 'axis' | 'joint_angle';
  /** How many degrees away from the demo still counts as right. */
  tolerance: number | null;
  /** 'default' = still the standard setting; 'coach' = the coach chose it. */
  tolerance_source: 'default' | 'coach';
}

/**
 * COACH-ONLY (backend `_WRITER_ONLY_METADATA_KEYS`). Which of this template's
 * angle checks still run the standard strictness, computed at finalize.
 *
 * `on_default` is AUTHORITATIVE: the client must never recompute it by
 * comparing a check's degrees against a default of its own, because the
 * default itself is configuration (HC-26) and a stale copy here would
 * misreport the coach's own template back to them.
 */
export interface ToleranceReview {
  on_default: number;
  coach_set: number;
  /** Every reviewable check, both sources. `on_default + coach_set === goals.length`. */
  goals: ToleranceReviewGoal[];
}

/**
 * Shape of `AnnotationTemplate.metadata` / `CreateTemplateRequest.metadata`.
 * Field list assembled from every actual read/write site (grepped, not
 * guessed): CreateTemplate.tsx's edit-mode hydration (~line 314-400) and its
 * save payload (~line 746-784), plus EvaluatePerformance.tsx's display reads
 * and MovementTemplate['metadata'] (types/api.ts) for the fields shared with
 * that parallel type. Kept as an explicit field list (no index signature) so
 * a new field is a deliberate, typed addition rather than a silent `any`.
 */
export interface TemplateMetadata {
  duration_seconds?: number;
  total_frames?: number;
  fps?: number;
  sport_category?: string;
  athlete_level?: string;
  coaching_cues?: string[];
  common_mistakes?: string[];
  performance_notes?: string[];
  camera_device?: string;
  camera_orientation?: string;
  /**
   * How the reference video was filmed. Closed vocabulary as of the B3 fix
   * (viewpoint observability Phase 1): 'side' | 'frontal' | 'diagonal' |
   * 'other'. The UI emits `frontal` (never `front`) and no longer offers
   * `behind`; free text now goes to `camera_angle_other` instead of being
   * stuffed into this key. Legacy templates may still carry `front` or a free
   * text string — the backend's single read point normalises `front` →
   * `frontal`.
   */
  camera_angle_setup?: string;
  /**
   * Free text for `camera_angle_setup === 'other'` (B3). Kept in its own key so
   * the vocabulary key stays a closed set that the backend can type as a
   * Literal and the narrative gate can match on.
   */
  camera_angle_other?: string | null;
  camera_view?: string;
  /** F3 facing direction, duplicated here (also top-level on the request) for backward compat with existing pipeline reads. */
  facing_direction?: 'left' | 'right' | null;
  /**
   * Where `facing_direction` came from — a coach's own choice outranks a
   * detector guess everywhere downstream.
   *   'detected'              — auto-applied from the detector, never shown to the coach
   *   'detected_unconfirmed'  — detector value kept because the coach closed the dialog
   *   'coach'                 — the coach picked or corrected it
   * Contract pin: null if and only if `facing_direction` is null.
   */
  facing_source?: 'detected' | 'detected_unconfirmed' | 'coach' | null;
  facing_confidence?: number;
  /**
   * COACH-ONLY (backend `_WRITER_ONLY_METADATA_KEYS`). Checkpoints whose
   * landmark set sits entirely on the side of the body away from the camera in
   * the reference video, computed at finalize. One entry per distinct
   * (label, landmark set). Never read by any LLM prompt builder.
   */
  far_side_note?: FarSideNoteEntry[] | null;
  /**
   * COACH-ONLY (backend `_WRITER_ONLY_METADATA_KEYS`). Verbatim rule-compiler
   * refusal texts, deduped — the coach wrote a rule the compiler could not turn
   * into a measurable check, and until now saw nothing about it.
   */
  refused_rule_texts?: string[] | null;
  /**
   * COACH-ONLY (backend `_WRITER_ONLY_METADATA_KEYS`). PR-8's sibling to
   * `refused_rule_texts`: the coach's verbatim phrase AND the reason beside it
   * (audit items `RC-13` / `E2E-9`).
   *
   * READERS PREFER THIS KEY and fall back to `refused_rule_texts`, which stays
   * a `string[]` with no reshape and no backfill (plan T-11) — so a template
   * finalized before PR-8 still renders its refusals, just without reasons.
   */
  refused_rule_details?: RefusedRuleDetail[] | null;
  /**
   * COACH-ONLY (backend `_WRITER_ONLY_METADATA_KEYS`). Rules the compiler found
   * and did not offer, with the disagreement named (`E2E-10`). Informational.
   */
  dropped_candidates?: DroppedCandidate[] | null;
  /**
   * COACH-ONLY (backend `_WRITER_ONLY_METADATA_KEYS`). Suggestions the coach
   * dismissed. Persisted so a reload does not resurrect every one of them
   * (frontend-audit vision gap 7 — they used to live in `useState`).
   */
  dismissed_suggestion_ids?: string[] | null;
  /**
   * COACH-ONLY (backend `_WRITER_ONLY_METADATA_KEYS`). Tolerance review
   * summary computed at finalize — which angle checks still use the standard
   * strictness. Absent on templates finalized before Phase 4.
   */
  tolerance_review?: ToleranceReview | null;
  handedness?: string;
  /** Legacy fallback location for phases/movement_type — kept alongside the top-level fields for backwards compatibility. */
  phases?: Phase[];
  movement_type?: string;
  global_body_match_enabled?: boolean;
  global_body_match_weight?: number;
  rotation_sensitive_scoring?: boolean;
  economy_of_motion_weight?: number;
  phase_scoring_config?: PhaseConfig[];
  phase_speed_intents?: Record<string, 'match' | 'reward_faster' | 'reward_slower' | 'off'>;
  /** P1-1 speed scoring master switch. Only ever written when 'on'; absent = 'off'. */
  speed_mode?: 'on' | 'off';
  /** P1-4 — only ever written alongside speed_mode: 'on'. */
  speed_weight?: number;
  mirror_invariant_scoring?: boolean;
  /** Read via `(meta.strictness as StrictnessLevel)` at the one call site that consumes it. */
  strictness?: string;
  /**
   * Goal Score PR-2/PR-7b — the coach-authored Form-effect curve. Absent,
   * empty, or invalid resolves to Neutral at score time
   * (`app/models/form_effect.py::resolve_form_effect_curve`) — never read
   * raw anywhere else. See `@/types/goalAuthoring`'s `FormEffectCurve`.
   */
  form_effect_curve?: import('@/types/goalAuthoring').FormEffectCurve;
}

export interface AnnotationTemplate {
  id: string;
  name: string;
  description?: string;
  movement_type?: string; // Optional
  reference_video_id: string;
  trim_start_frame: number;
  trim_end_frame: number;
  /** Index of the person to track when multiple people appear in the reference video */
  focal_person_id?: number;
  coach_notes?: string;
  annotations?: Annotation[];
  phases?: Phase[]; // Phase information if available
  regions?: Region[]; // Region information if available
  /**
   * Known error patterns for this movement type, coach-authored free text
   * (backend TemplateCreate.common_mistakes). Gates visibility of the
   * "Compile coach rules" action (Rule Compiler, contract §6) alongside
   * calibration-take reason_text.
   *
   * Typed without `| null` to match the existing `common_mistakes?: string[]`
   * on MovementTemplate (types/api.ts), which several pages assign an
   * AnnotationTemplate into structurally — the backend can return JSON
   * `null` (Optional[List[str]]) but all readers already treat it via
   * optional chaining, so this stays consistent with the pre-existing
   * (slightly loose) convention rather than widening a type three unrelated
   * call sites depend on.
   */
  common_mistakes?: string[];
  /** Whether the movement has a clear lead side (asymmetric), is bilateral (symmetric), or rotates 360°+ (rotational). */
  exercise_symmetry?: 'asymmetric' | 'symmetric' | 'rotational';
  /** Which side leads in the reference video. Meaningful when exercise_symmetry === 'asymmetric' or 'rotational'. */
  template_lead_side?: 'left' | 'right' | null;
  /**
   * The direction the performer's front points in the reference video as
   * captured — image-left or image-right. Two copies, one value: the request's
   * top-level field is written into metadata at create/update, and on read the
   * top-level is back-filled from metadata, so the two are equal on every
   * template finalized after the Phase 1 contract. Readers may use either;
   * prefer `metadata.facing_direction` with this as the fallback.
   */
  facing_direction?: 'left' | 'right' | null;
  /**
   * When true, the evaluation pipeline adds coaching tips about the ORDER body segments
   * fire (e.g. "core before arm", "hip before knee"). Opt-in, default false.
   * Only affects coaching narrative — never changes the numeric score.
   */
  enable_sequencing_coaching?: boolean;
  /**
   * Template visibility (Security P2-1).
   * "private" (default): only owner/supporters/channel-members/admin can access.
   * "public": anyone including unauthenticated callers can read this template.
   */
  visibility?: 'private' | 'public';
  /**
   * Archive state (D12). Archived templates are excluded from the active-template
   * cap and hidden from the default template list. Sourced from TemplateIndex.
   */
  is_archived?: boolean;
  /** Optional YouTube reference video URL attached to this template (YTI-1). */
  youtube_url?: string | null;
  /**
   * Teaching video linked to this template via POST /api/templates/{id}/teaching-video.
   * Injected by the backend get_template/list_templates endpoints from TemplateIndex.
   */
  teaching_video_id?: string | null;
  teaching_video_source_type?: 'video' | 'audio' | 'paste' | null;
  metadata?: TemplateMetadata;
  /**
   * Channel this template is attached to, if any.
   * Set via POST /api/templates?channel_id=<uuid> or PATCH /api/templates/{id}/channel.
   */
  channel_id?: string | null;
  channel_slug?: string | null;
  /** Display name of the channel this template belongs to. Preferred over channel_slug for display. */
  channel_name?: string | null;
  /**
   * Average LLM coaching faithfulness score for evaluations against this template (0-1 scale).
   * Injected by the backend list endpoint from EvaluationIndex aggregate. Null when
   * the template has no evaluations or faithfulness scoring has not been computed.
   */
  avg_coaching_faithfulness?: number | null;
  /**
   * Template kind (PTD-1/PTD-2).
   * 'solo' (default): standard single-person template.
   * 'positional': multi-figure template for team drills.
   */
  template_kind?: 'solo' | 'positional';
  /**
   * Figures for positional templates. Null/absent for solo templates.
   */
  figures?: FigureCreate[] | null;
  created_at: string;
  updated_at?: string;
  /**
   * Response-only rule-channel divert signal (backend
   * `Template.rule_channel_diverted` / `RuleChannelDivertedItem`,
   * app/api/template_routes.py). Present ONLY on the PUT /api/templates/{id}
   * response of a call whose accept-time hook (SPORTFORM_RULE_CHANNEL_MINTING)
   * diverted one or more newly-submitted compiled-rule annotations to
   * non-scoring TakeCritiqueRecords instead of appending them as duplicate
   * scoring annotations — undefined/null on every other response (GET,
   * unrelated PUTs, flag off, nothing diverted this call). Never persisted —
   * do not round-trip this field back into a subsequent PUT body.
   */
  rule_channel_diverted?: RuleChannelDivertedItem[] | null;
}

/**
 * One compiled-rule accept that the backend diverted to a non-scoring
 * TakeCritiqueRecord instead of appending it as a duplicate scoring
 * annotation. Mirrors backend `RuleChannelDivertedItem`
 * (app/api/template_routes.py) field-for-field — enough for the UI to name
 * what happened to a specific accept click.
 */
export interface RuleChannelDivertedItem {
  /** The id of the newly-submitted annotation that was excluded (not appended). */
  annotation_id: string;
  /** The diverted annotation's goal_type (e.g. 'joint_angle_goal'), or null if unresolvable. */
  goal_key: string | null;
  /** The diverted annotation's coach-facing label, if any. */
  label: string | null;
  /** The id of the existing accepted annotation sharing this rule's channel identity. */
  duplicate_of_annotation_id: string;
  /** The calibration take this diverted accept's evidence concerns, if any. */
  take_id: string | null;
}

/**
 * One figure in a positional template (PTD-2/PTD-3).
 * Mirrors the backend FigureCreate Pydantic model — match exactly.
 */
export interface FigureCreate {
  /** Short position label (1–10 chars, unique per template). E.g. "PG", "GK". */
  position_label: string;
  reference_video_id: string;
  /** Index of the detected person to use as this figure's reference performer. */
  focal_person_id: number | null;
  trim_start_frame: number;
  trim_end_frame: number | null;
  annotations: Annotation[] | null;
  phases: Phase[] | null;
  coach_notes: string | null;
  coaching_cues: string[] | null;
}

export interface CreateTemplateRequest {
  name: string;
  description?: string;
  movement_type?: string; // Optional
  reference_video_id: string;
  trim_start_frame: number;
  trim_end_frame: number;
  /** Index of the person to track when multiple people appear in the reference video */
  focal_person_id?: number;
  coach_notes?: string;
  annotations?: Annotation[];
  phases?: Phase[]; // Root-level phases for backend finalization
  /** Whether the movement has a clear lead side (asymmetric), is bilateral (symmetric), or rotates 360°+ (rotational). */
  exercise_symmetry?: 'asymmetric' | 'symmetric' | 'rotational';
  /** Which side leads in the reference video. Meaningful when exercise_symmetry === 'asymmetric' or 'rotational'. Null when symmetric. */
  template_lead_side?: 'left' | 'right' | null;
  /**
   * When true, the evaluation pipeline adds coaching tips about the ORDER body segments
   * fire (e.g. "core before arm", "hip before knee"). Opt-in, default false.
   * Only affects coaching narrative — never changes the numeric score.
   */
  enable_sequencing_coaching?: boolean;
  /**
   * Template kind (PTD-1/PTD-2).
   * 'solo' (default): standard single-person template.
   * 'positional': multi-figure template for team drills.
   */
  template_kind?: 'solo' | 'positional';
  /**
   * Required when template_kind === 'positional'. 2–5 figures with unique position_label values.
   * Omit entirely (do NOT send null/[]) for solo templates.
   */
  figures?: FigureCreate[];
  metadata?: TemplateMetadata;
}

/**
 * Get all annotation-based templates.
 * @param includeArchived When true, also returns archived templates (D12).
 *   Default (false) excludes them.
 */
export async function getTemplates(
  includeArchived = false,
): Promise<AnnotationTemplate[]> {
  const response = await apiClient.get<AnnotationTemplate[]>('/templates', {
    params: includeArchived ? { include_archived: true } : undefined,
  });
  return response.data;
}

/**
 * Get a single annotation-based template by ID
 */
export async function getTemplate(templateId: string): Promise<AnnotationTemplate> {
  const response = await apiClient.get<AnnotationTemplate>(`/templates/${templateId}`);
  return response.data;
}

/**
 * Create an annotation-based template.
 * Pass an optional channelId to associate the template with a channel at creation time.
 * The backend accepts channel_id as a query param on POST /api/templates.
 */
export async function createTemplate(
  request: CreateTemplateRequest,
  channelId?: string,
): Promise<{ template: AnnotationTemplate }> {
  const params = channelId ? { channel_id: channelId } : undefined;
  const response = await apiClient.post<{ template: AnnotationTemplate }>('/templates', request, { params });
  return response.data;
}

/**
 * Update an annotation-based template
 */
export async function updateTemplate(
  templateId: string,
  request: Partial<CreateTemplateRequest>
): Promise<AnnotationTemplate> {
  const response = await apiClient.put<AnnotationTemplate>(`/templates/${templateId}`, request);
  return response.data;
}

/**
 * Delete an annotation-based template
 */
export async function deleteTemplate(templateId: string): Promise<void> {
  await apiClient.delete(`/templates/${templateId}`);
}

/**
 * Bulk delete annotation-based templates by ID list
 */
export async function bulkDeleteTemplates(templateIds: string[]): Promise<BulkDeleteResponse> {
  const response = await apiClient.post<BulkDeleteResponse>('/templates/bulk-delete', {
    template_ids: templateIds,
  });
  return response.data;
}

/**
 * Delete all annotation-based templates
 */
export async function deleteAllTemplates(): Promise<DeleteAllResponse> {
  const response = await apiClient.delete<DeleteAllResponse>('/templates/all', {
    params: { confirmation: 'DELETE_ALL_TEMPLATES' },
  });
  return response.data;
}

/**
 * Update the channel a template belongs to.
 * Pass null to detach the template from any channel.
 * Requires the caller to be the template owner or admin.
 *
 * Backend: PATCH /api/templates/{id}/channel
 * Body:   { channel_id: string | null }
 */
export async function updateTemplateChannel(
  templateId: string,
  channelId: string | null,
): Promise<{ channel_id: string | null; channel_slug: string | null }> {
  const response = await apiClient.patch<{ channel_id: string | null; channel_slug: string | null }>(
    `/templates/${templateId}/channel`,
    { channel_id: channelId },
  );
  return response.data;
}

/**
 * Update template visibility (Security P2-1).
 * Only the template owner, supporters, or admin may call this.
 */
export async function updateTemplateVisibility(
  templateId: string,
  visibility: 'private' | 'public',
): Promise<AnnotationTemplate> {
  const response = await apiClient.patch<AnnotationTemplate>(
    `/templates/${templateId}/visibility`,
    { visibility },
  );
  return response.data;
}

/**
 * Archive a template (D12). Archived templates are excluded from the
 * active-template cap and hidden from the default template list.
 * Only the template owner, supporters, or admin may call this.
 */
export async function archiveTemplate(
  templateId: string,
): Promise<AnnotationTemplate> {
  const response = await apiClient.patch<AnnotationTemplate>(
    `/templates/${templateId}/archive`,
    {},
  );
  return response.data;
}

/**
 * Un-archive a template (D12), returning it to the active set. Rejected with
 * 409 if the coach is already at their tier's active-template cap.
 * Only the template owner, supporters, or admin may call this.
 */
export async function unarchiveTemplate(
  templateId: string,
): Promise<AnnotationTemplate> {
  const response = await apiClient.patch<AnnotationTemplate>(
    `/templates/${templateId}/unarchive`,
    {},
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// NOTE: The Tier 1 "suggest a Stance Orientation gate, wait for a coach
// Accept click" flow (SuggestedStanceAnnotation + acceptStanceGateSuggestion,
// POST /templates/{id}/stance-suggestion/accept) was retired 2026-08-09.
// automatic-orientation-epic Phase 1 (backend shipped 2026-08-07) made
// finalize MATERIALIZE the gate directly instead of suggesting it, so
// `suggested_annotations` on the finalize response always resolves to `[]`
// and this accept call could never fire. See
// backlog/automatic-orientation-epic.md ("Phase 1 REMAINDER").
//
// PR-5 / owner decision D12 closes the whole story: the replacement
// AUTO-provisioning path is deleted on the backend, and its notice component
// is deleted here, because a hard limit's failure angle must come from the
// coach or from their own clips — never from a limit the system picked
// (REFACTOR_PLAN.md §C.3c). `AutoProvisionedStanceGate` and the
// `auto_provisioned_stance_gate` wire field stay in phaseApi.ts for
// compatibility while both halves ship; nothing renders them.
// ---------------------------------------------------------------------------
// P1-4 — Speed before/after score preview (R7 safeguard, B-2026-0123)
// ---------------------------------------------------------------------------

/**
 * Result shape from POST /api/templates/{id}/speed-preview.
 * "ok"        — a representative athlete eval was found; before/after/delta populated.
 * "no_sample" — no evaluated athlete exists yet; coach can still save.
 */
export interface SpeedPreviewResult {
  status: 'ok' | 'no_sample';
  sample_label: string | null;
  before_overall: number | null;
  after_overall: number | null;
  delta: number | null;
}

/**
 * Fetch a before/after overall_score preview for the given template when speed
 * scoring is enabled at the specified weight.
 *
 * Only meaningful in edit mode (the template must be saved first so the backend
 * can find an existing athlete evaluation to re-score).
 */
export async function fetchSpeedPreview(
  templateId: string,
  speedWeight: number,
): Promise<SpeedPreviewResult> {
  const response = await apiClient.post<SpeedPreviewResult>(
    `/templates/${templateId}/speed-preview`,
    { speed_weight: speedWeight },
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Travel-distance preview (travel-distance plan v2.1 §5.2, PR-A).
//
// Field names are BYTE-IDENTICAL to
// `backend/app/api/template_routes.py::TravelPreviewRequest`/
// `TravelPreviewResponse` and `docs/coach_input/TRAVEL_PR_A_CONTRACT.md` §2.
// ---------------------------------------------------------------------------

/** What the coach has already picked in the studio flow (landmark -> phase
 * -> A/B) before asking for a measurement. */
export interface TravelPreviewRequest {
  /** The tracked landmark (anatomical or lead_/trail_ functional name). */
  landmark: string;
  /** Phase label to scope the measurement to. Omit/null = the whole clip. */
  target_phase?: string | null;
  /** Pin a specific body-height definition instead of letting it cascade. */
  body_height_definition?: BodyHeightDefinition | null;
}

/**
 * The demo's measured travel ratio. Always a 200 — a measurable failure is
 * `status: "abstained"` with a `reason`, never a 4xx/5xx.
 *
 * Rounding: `ratio`/`body_height` are already rounded to 2dp by the backend
 * — do not round again or you will mask the "noise past 2dp" rule the
 * contract states (§2).
 */
export interface TravelPreviewResponse {
  ratio: number | null;
  /** The denominator, for curiosity/debugging only — never show as "their
   * height in cm"; it is NOT a stature estimate. */
  body_height: number | null;
  definition_used: BodyHeightDefinition | null;
  frames_used: number;
  status: 'computed' | 'abstained';
  /** Present iff status === 'abstained'. Already coach-facing plain
   * language — safe to render directly (contract §4a). */
  reason: string | null;
  /**
   * PR-B item 5 (contract §2.1): the tracked landmark's per-frame path over
   * the SAME measured window, as `[frame_index, x, y]` — one entry per
   * frame the landmark was visible on. `null` whenever `status ===
   * 'abstained'`. Normalised [0,1], in the template's own, NEVER-mirrored
   * reference-clip frame (there is no "other side" to mirror against —
   * this preview measures the template's own clip against itself).
   */
  trajectory_points: Array<[number, number, number]> | null;
}

/**
 * Measure the coach's own demonstrated travel-distance ratio (cache-only —
 * never triggers a pose-extraction run). Call on an explicit "measure" action
 * and whenever the landmark or phase selection changes; never on every
 * keystroke (contract §2's own instruction — a cache hit can still cost an
 * R2 cold download on first touch).
 *
 * 403 when the caller cannot read this template (not the owner / not a
 * channel member); 404 when the template does not exist.
 */
export async function previewTravelTarget(
  templateId: string,
  request: TravelPreviewRequest,
): Promise<TravelPreviewResponse> {
  const response = await apiClient.post<TravelPreviewResponse>(
    `/templates/${templateId}/goals/preview-travel`,
    request,
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Calibration Takes (T6, B-2026-0126)
// ---------------------------------------------------------------------------

const CAL_TAKES_BASE = (templateId: string) =>
  `/templates/${templateId}/calibration-takes`;

/**
 * Upload a calibration take video.
 * POST /api/templates/{id}/calibration-takes/upload
 * Returns {take_id, status: "uploaded"}.
 *
 * `reasonText` is the coach's optional explanation of why this take is
 * labeled the way it is (max 2000 chars). Sent as the `reason_text` form
 * field only when non-empty — omitted entirely otherwise (contract with
 * the backend calibration-take upload endpoint).
 *
 * `gradeLabel` (grade-feel scoring plan, Story 4) is the owner-band grade
 * ('A'|'B'|'C'|'D'|'F') — a SEPARATE control from `label`, valid alongside
 * ANY label. Sent as `grade_label` only when set — omitted entirely
 * otherwise. LEGACY as of numeric take-grade entry: CalibrationTakeUploader
 * no longer collects this; the parameter stays for API completeness/other
 * callers.
 *
 * `gradeNumeric` (numeric take-grade entry) is the 0-100 numeric grade —
 * a SEPARATE, ADDITIVE control from both `label` and `gradeLabel`. Sent as
 * `grade_numeric` only when non-null. This is what the upload form actually
 * sends today.
 */
export async function uploadCalibrationTake(
  templateId: string,
  file: File,
  label: CalibrationTakeLabel,
  failureDimension?: 'motion' | 'activity' | 'angle' | null,
  reasonText?: string | null,
  gradeLabel?: CalibrationGradeLabel | null,
  gradeNumeric?: number | null,
): Promise<CalibrationTakeUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('label', label);
  if (failureDimension) {
    formData.append('failure_dimension', failureDimension);
  }
  const trimmedReason = reasonText?.trim();
  if (trimmedReason) {
    formData.append('reason_text', trimmedReason);
  }
  if (gradeLabel) {
    formData.append('grade_label', gradeLabel);
  }
  if (gradeNumeric !== null && gradeNumeric !== undefined) {
    formData.append('grade_numeric', String(gradeNumeric));
  }
  const response = await apiClient.post<CalibrationTakeUploadResponse>(
    `${CAL_TAKES_BASE(templateId)}/upload`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
}

/**
 * Trigger async pose extraction + validation for a calibration take.
 * POST /api/templates/{id}/calibration-takes/{take_id}/process
 * Returns 202 with status='processing'.
 */
export async function processCalibrationTake(
  templateId: string,
  takeId: string,
): Promise<CalibrationTake> {
  const response = await apiClient.post<CalibrationTake>(
    `${CAL_TAKES_BASE(templateId)}/${takeId}/process`,
  );
  return response.data;
}

/**
 * Poll the processing status of a calibration take.
 * GET /api/templates/{id}/calibration-takes/{take_id}/status
 */
export async function getCalibrationTakeStatus(
  templateId: string,
  takeId: string,
): Promise<CalibrationTake> {
  const response = await apiClient.get<CalibrationTake>(
    `${CAL_TAKES_BASE(templateId)}/${takeId}/status`,
  );
  return response.data;
}

/**
 * List all calibration takes for a template with calibration summary.
 * GET /api/templates/{id}/calibration-takes/
 */
export async function listCalibrationTakes(
  templateId: string,
): Promise<CalibrationTakeListResponse> {
  const response = await apiClient.get<CalibrationTakeListResponse>(
    `${CAL_TAKES_BASE(templateId)}/`,
  );
  return response.data;
}

/**
 * Delete a calibration take. Triggers re-calibration.
 * DELETE /api/templates/{id}/calibration-takes/{take_id}
 */
export async function deleteCalibrationTake(
  templateId: string,
  takeId: string,
): Promise<CalibrationTakeDeleteResponse> {
  const response = await apiClient.delete<CalibrationTakeDeleteResponse>(
    `${CAL_TAKES_BASE(templateId)}/${takeId}`,
  );
  return response.data;
}

/**
 * Edit a calibration take's declared lead side and/or failure-dimension
 * tags (take-edit-frontend). PATCH /api/templates/{id}/calibration-takes/{take_id}
 *
 * `updates` must carry at least one key or the API 422s — never call this
 * with `{}`. Distinguish "leave unchanged" (omit the key) from "clear the
 * field" (`null`) at the call site; both survive the wire because axios's
 * default JSON serialization drops `undefined` keys but keeps explicit
 * `null` ones.
 *
 * Triggers re-calibration server-side, same as delete — the returned take
 * (and the subsequent list refetch) typically shows status flipping back
 * toward 'processing'.
 */
export async function updateCalibrationTake(
  templateId: string,
  takeId: string,
  updates: CalibrationTakeUpdateRequest,
): Promise<CalibrationTake> {
  const response = await apiClient.patch<CalibrationTake>(
    `${CAL_TAKES_BASE(templateId)}/${takeId}`,
    updates,
  );
  return response.data;
}

/**
 * Get the current calibration summary for a template.
 * GET /api/templates/{id}/calibration-takes/calibration-status
 * Returns null when no calibration has been computed yet.
 */
export async function getCalibrationStatus(
  templateId: string,
): Promise<CalibrationSummary | null> {
  const response = await apiClient.get<CalibrationSummary | null>(
    `${CAL_TAKES_BASE(templateId)}/calibration-status`,
  );
  return response.data;
}

/**
 * Persist the strictness dial for a template's calibration.
 * PATCH /api/templates/{id}/calibration-takes/calibration-strictness
 *
 * Stored as metadata.calibration_strictness; applied as a bounded ±15% nudge
 * on the calibrated tolerance ONLY when take-derived calibration is active.
 */
export async function saveCalibrationStrictness(
  templateId: string,
  strictness: 'lenient' | 'standard' | 'strict',
): Promise<void> {
  await apiClient.patch(
    `${CAL_TAKES_BASE(templateId)}/calibration-strictness`,
    { strictness },
  );
}

/**
 * Set or clear a coach-typed hard limit for one pricing dimension (Goal F,
 * EXP-010 A7b). PATCH /api/templates/{id}/calibration-takes/pricing-coach-limits
 *
 * `limit: null` (or omitted) clears the dimension's typed limit, falling
 * back to the minted line learned from the coach's example videos, or to
 * no line at all. Stored at metadata.pricing_coach_limits[dim], OUTSIDE
 * metadata.calibration — a re-mint never clobbers it.
 */
export async function savePricingCoachLimit(
  templateId: string,
  dim: PricingDimName,
  limit: number | null,
): Promise<void> {
  await apiClient.patch(
    `${CAL_TAKES_BASE(templateId)}/pricing-coach-limits`,
    { dim, limit },
  );
}

/**
 * Explicit "Re-measure takes" action (backlog: recompile-button-remeasure-gap.md).
 * POST /api/templates/{id}/calibration-takes/recalibrate
 *
 * Schedules the SAME recalibration chain the take-PATCH/DELETE paths already
 * trigger server-side — fire-and-forget, no job id on the wire. Returns 202
 * unconditionally; the caller polls `getCalibrationStatus` and watches
 * `computed_at` for a change to detect completion (see useRecalibrateTakes).
 */
export async function recalibrateTakes(
  templateId: string,
): Promise<CalibrationRecalibrateResponse> {
  const response = await apiClient.post<CalibrationRecalibrateResponse>(
    `${CAL_TAKES_BASE(templateId)}/recalibrate`,
  );
  return response.data;
}

/**
 * Edit a calibration take's Example Performances visibility/consent/note
 * fields (PR 1+2). This is a thin, narrowly-named alias over
 * `updateCalibrationTake` — same endpoint, same wire contract — kept
 * separate only so call sites that touch ONLY visibility state read as
 * such. Prefer `updateCalibrationTake` directly for lead-side/dimension/
 * grade/label edits; either name reaches the identical PATCH.
 */
export const patchCalibrationTake = updateCalibrationTake;

// ---------------------------------------------------------------------------
// Example Performances (backlog/example-performances-template-resources.md,
// PR 1+2, owner-approved 2026-09-23)
// ---------------------------------------------------------------------------

/**
 * List the athlete-visible "example performances" for a template.
 * GET /api/templates/{id}/calibration-takes/visible
 *
 * Returns ONLY takes the coach has opted in (`is_athlete_visible=true`) AND
 * consented to (`visibility_consent_given_at` set) — the backend applies
 * this filter server-side, so an empty `performances` array is the normal,
 * expected shape for a template with no exposed takes (render nothing, not
 * an error).
 */
export async function listVisibleExamplePerformances(
  templateId: string,
): Promise<ExamplePerformanceListResponse> {
  const response = await apiClient.get<ExamplePerformanceListResponse>(
    `${CAL_TAKES_BASE(templateId)}/visible`,
  );
  return response.data;
}

/**
 * Mint a short-lived signed-URL token for an example performance's video
 * (kind="calibration_take"). GET /api/templates/{id}/calibration-takes/{take_id}/media-token
 *
 * Mirrors `fetchMediaToken`/`fetchTeachingMediaToken` (videoApi.ts /
 * teachingVideoApi.ts) — see MEDIA_AUTH_CONTRACT.md. Requires the bearer
 * header, which this call carries as usual (it goes through apiClient, not
 * a bare <video>/<img> tag).
 */
export async function fetchCalibrationTakeMediaToken(
  templateId: string,
  takeId: string,
): Promise<MediaTokenResponse> {
  const response = await apiClient.get<MediaTokenResponse>(
    `${CAL_TAKES_BASE(templateId)}/${takeId}/media-token`,
  );
  return response.data;
}

/**
 * Build the signed streaming URL for an example performance's video from a
 * previously-minted token. Never build this URL without a token — a bare
 * `<video src>` built from the unsigned endpoint will 401 (the route
 * requires a valid `sig`+`exp` pair or a bearer-authenticated request that a
 * `<video>` tag can never send).
 */
export function buildSignedCalibrationTakeStreamUrl(
  templateId: string,
  takeId: string,
  token: MediaTokenResponse,
): string {
  return `${apiClient.defaults.baseURL}${CAL_TAKES_BASE(templateId)}/${takeId}/stream?sig=${token.sig}&exp=${token.exp}`;
}

// ---------------------------------------------------------------------------
// Rule Compiler (Phase 3, coach-knowledge-codification epic)
// docs/RULE_COMPILER_CONTRACT.md §4
// ---------------------------------------------------------------------------

/**
 * Kick off compiling the template's calibration-take reasons and common
 * mistakes into candidate scoring rules. Starts an async background job on
 * the backend (same asyncio pattern as calibration-take processing) and
 * returns 202 immediately. Poll `getCompileRulesStatus` for progress/results.
 *
 * POST /api/templates/{id}/compile-rules
 */
export async function compileRules(
  templateId: string,
): Promise<CompileRulesKickoffResponse> {
  const response = await apiClient.post<CompileRulesKickoffResponse>(
    `/templates/${templateId}/compile-rules`,
  );
  return response.data;
}

/**
 * Poll the status of the most recent compile-rules run for a template.
 * Suggestions are replaced wholesale on each run (contract §4) — this
 * always returns the latest run's full suggestion list, never a merge of
 * past runs.
 *
 * GET /api/templates/{id}/compile-rules/status
 */
export async function getCompileRulesStatus(
  templateId: string,
): Promise<CompileRulesStatusResponse> {
  const response = await apiClient.get<CompileRulesStatusResponse>(
    `/templates/${templateId}/compile-rules/status`,
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Batch accept / dismiss (PR-8, audit item RC-14; owner decisions D4 + D10)
// ---------------------------------------------------------------------------

/**
 * Add a batch of compiled-rule suggestions to the template, transactionally.
 *
 * POST /api/templates/{id}/compile-rules/accept
 *
 * This replaces accepting one suggestion at a time through a whole-template
 * `PUT` of the annotations array. Three things that path could not do:
 *   · **Be a batch.** The coach reviewed and committed one card at a time, and
 *     the client needed a panel-wide lock so a second click could not read a
 *     pre-mutation annotations array and silently drop the first write.
 *   · **Guard a duplicate.** Nothing stopped the same check landing twice,
 *     where both copies then take marks off for the same mistake (`TV-21`).
 *   · **Refuse a stale suggestion.** A suggestion built by an older
 *     materialiser was accepted on trust (D10a / plan N-7).
 *
 * `dryRun` previews: the server writes nothing and returns the same shape, so
 * the coach reads "this will add 3 and skip 1 — here's why" BEFORE committing.
 *
 * `overrides` carries the coach's own corrections to the classifier, keyed by
 * suggestion id (see `CompileRulesAcceptRequest.overrides`). It is passed
 * through unchanged and sent on the dry run as well as the commit; omitted
 * entirely when the coach changed nothing, so the ordinary request stays the
 * contract's minimal form.
 *
 * Errors are deliberately NOT caught here. A 409 whose `detail.kind` is
 * `stale_suggestion` is a meaningful answer the panel turns into a rebuild
 * prompt (see `RuleCompilerPanel`), not a failure to swallow.
 */
export async function acceptCompiledRules(
  templateId: string,
  suggestionIds: string[],
  dryRun = false,
  overrides?: Record<string, AcceptSentimentOverride>,
): Promise<CompileRulesAcceptResponse> {
  const body: CompileRulesAcceptRequest = {
    suggestion_ids: suggestionIds,
    // Omitted rather than `false` on a commit: the backend's default IS commit,
    // and sending the key only when previewing keeps "what a commit looks like
    // on the wire" identical to the contract's minimal form.
    ...(dryRun ? { dry_run: true } : {}),
    // Same rule: absent when the coach corrected nothing.
    ...(overrides && Object.keys(overrides).length > 0 ? { overrides } : {}),
  };
  const response = await apiClient.post<CompileRulesAcceptResponse>(
    `/templates/${templateId}/compile-rules/accept`,
    body,
  );
  return response.data;
}

/**
 * Remember that the coach dismissed these suggestions.
 *
 * POST /api/templates/{id}/compile-rules/dismiss
 *
 * Dismissal used to live in a `useState` set, so a reload resurrected every
 * dismissed suggestion (frontend-audit vision gap 7). The server now holds the
 * ids, and a dismissed suggestion is also refused at accept time.
 */
export async function dismissCompiledRules(
  templateId: string,
  suggestionIds: string[],
): Promise<CompileRulesDismissResponse> {
  const body: CompileRulesDismissRequest = { suggestion_ids: suggestionIds };
  const response = await apiClient.post<CompileRulesDismissResponse>(
    `/templates/${templateId}/compile-rules/dismiss`,
    body,
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Focus-area placement assistance (PR-4, owner decision D5)
// ---------------------------------------------------------------------------

/**
 * Ask where a resolved focus-area region's body points sit in the reference
 * video, so the coach can drop a box onto the canvas in one click.
 *
 * GET /api/templates/{id}/regions/{annotation_id}/placement
 *
 * Why this is a request and not a field on the annotation: owner decision D5
 * makes canvas-less the canonical state. A region scores from its body points,
 * not from a box, so a box is derived only when the coach asks for one. The
 * client this replaced invented a box at (0.4, 0.4, 0.2 x 0.2) for every
 * suggestion, which looked to the coach like a position someone had chosen.
 *
 * The response's `box` is in NORMALISED 0-1 canvas coordinates and is `null`
 * whenever no box can be derived (an unresolved region, or body points that
 * were not readable in the reference frame). A `null` box must stay null in
 * the UI — substituting a default would reintroduce exactly the placeholder
 * D5 removed.
 */
export async function getRegionPlacement(
  templateId: string,
  annotationId: string,
): Promise<RegionPlacement> {
  const response = await apiClient.get<RegionPlacement>(
    `/templates/${templateId}/regions/${annotationId}/placement`,
  );
  return response.data;
}
